import React, { useMemo } from 'react';
import {
  fillDailyRange,
  formatDelta,
  formatMoney,
  formatShortDate,
  paymentMethodLabel,
  summarizeDailySeries,
} from './analyticsUtils';
import { buildAnalyticsInsights, formatOrderStatusLabel } from './analyticsInsights';

const ANALYTICS_VIEW_KEY = 'saloni_admin_analytics_view';

export function readAnalyticsViewMode() {
  try {
    return localStorage.getItem(ANALYTICS_VIEW_KEY) === 'detailed' ? 'detailed' : 'simple';
  } catch {
    return 'simple';
  }
}

export function writeAnalyticsViewMode(mode) {
  try {
    localStorage.setItem(ANALYTICS_VIEW_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function AnalyticsViewToggle({ viewMode, onChange }) {
  return (
    <div className="admin-analytics-view-toggle mb-3" role="group" aria-label="Dashboard detail level">
      <span className="admin-analytics-view-toggle__label">View</span>
      <button
        type="button"
        className={`btn btn-sm${viewMode === 'simple' ? ' btn-primary' : ' btn-outline-secondary'}`}
        onClick={() => onChange('simple')}
      >
        Simple
      </button>
      <button
        type="button"
        className={`btn btn-sm${viewMode === 'detailed' ? ' btn-primary' : ' btn-outline-secondary'}`}
        onClick={() => onChange('detailed')}
      >
        Detailed
      </button>
      <span className="admin-analytics-view-toggle__hint small text-muted">
        {viewMode === 'simple'
          ? 'Highlights what matters most — expand for full breakdowns.'
          : 'All metrics, tables, and payment geography.'}
      </span>
    </div>
  );
}

export function AnalyticsJumpNav({ viewMode }) {
  const links =
    viewMode === 'simple'
      ? [
          { id: 'analytics-glance', label: 'Summary' },
          { id: 'analytics-actions', label: 'To-do' },
          { id: 'analytics-insights', label: 'Takeaways' },
          { id: 'analytics-trend', label: 'Trend' },
          { id: 'analytics-recent', label: 'Recent orders' },
        ]
      : [
          { id: 'analytics-glance', label: 'Summary' },
          { id: 'analytics-actions', label: 'To-do' },
          { id: 'analytics-insights', label: 'Takeaways' },
          { id: 'analytics-pulse', label: 'Pulse' },
          { id: 'analytics-period', label: 'Period stats' },
          { id: 'analytics-trend', label: 'Trend' },
          { id: 'analytics-operations', label: 'Shipping' },
          { id: 'analytics-payments', label: 'Payments' },
          { id: 'analytics-products', label: 'Products' },
          { id: 'analytics-recent', label: 'Recent' },
          { id: 'analytics-gift', label: 'Gift finder' },
        ];
  return (
    <nav className="admin-analytics-jump mb-3" aria-label="Jump to section">
      {links.map((l) => (
        <a key={l.id} className="admin-analytics-jump__link" href={`#${l.id}`}>
          {l.label}
        </a>
      ))}
    </nav>
  );
}

export function AnalyticsCollapsibleSection({ id, title, hint, defaultOpen = false, children }) {
  return (
    <details id={id} className="admin-analytics-fold card border-0 shadow-sm mb-3" open={defaultOpen || undefined}>
      <summary className="admin-analytics-fold__summary card-body py-3">
        <span className="admin-analytics-fold__title">{title}</span>
        {hint ? <span className="admin-analytics-fold__hint small text-muted">{hint}</span> : null}
      </summary>
      <div className="card-body pt-0">{children}</div>
    </details>
  );
}

export function AnalyticsQuickPulse({ revenue, orders, avgOrderValue }) {
  const rev = revenue || {};
  const ord = orders || {};
  const tiles = [
    { label: 'Today', value: formatMoney(rev.today), sub: `${ord.today ?? 0} paid`, accent: true },
    { label: '7 days', value: formatMoney(rev.week), sub: `${ord.week ?? 0} orders` },
    { label: '30 days', value: formatMoney(rev.month), sub: `${ord.month ?? 0} orders` },
    {
      label: 'All time',
      value: formatMoney(rev.all_time),
      sub: `Avg ${formatMoney(avgOrderValue)} · ${ord.all_time ?? 0} orders`,
    },
  ];
  return (
    <section id="analytics-pulse" className="admin-analytics-quick-pulse mb-3" aria-label="Quick pulse">
      <p className="admin-analytics-quick-pulse__eyebrow small text-muted mb-2">
        Rolling totals (not limited to the date range above)
      </p>
      <div className="admin-analytics-quick-pulse__grid">
        {tiles.map((t) => (
          <div
            key={t.label}
            className={`admin-analytics-quick-pulse__tile${t.accent ? ' admin-analytics-quick-pulse__tile--accent' : ''}`}
          >
            <span className="admin-analytics-quick-pulse__label">{t.label}</span>
            <span className="admin-analytics-quick-pulse__value">{t.value}</span>
            <span className="admin-analytics-quick-pulse__sub small text-muted">{t.sub}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function AnalyticsDailyChart({ daily, from, to, metric }) {
  const filled = useMemo(() => fillDailyRange(daily, from, to), [daily, from, to]);
  const summary = useMemo(() => summarizeDailySeries(filled, metric), [filled, metric]);
  const useOrders = metric === 'orders';
  const maxVal = Math.max(1, ...filled.map((d) => (useOrders ? d.orders : d.revenue)));
  const peakDate = summary?.peak?.date;

  if (!filled.length) {
    return <p className="small text-muted mb-0">Pick a valid date range to see the chart.</p>;
  }

  if (summary?.quiet) {
    return (
      <div className="admin-analytics-chart-empty">
        <p className="mb-1 fw-semibold">No paid sales in this period</p>
        <p className="small text-muted mb-0">Try a wider range, or check Orders for pending checkouts.</p>
      </div>
    );
  }

  return (
    <>
      <div className="admin-analytics-chart" aria-label={useOrders ? 'Daily orders chart' : 'Daily revenue chart'}>
        <div className="admin-analytics-chart__bars">
          {filled.map((d) => {
            const val = useOrders ? d.orders : d.revenue;
            const h = Math.max(4, Math.round((val / maxVal) * 100));
            const isPeak = peakDate && d.date === peakDate && val > 0;
            const title = useOrders
              ? `${formatShortDate(d.date)}: ${d.orders} order(s)`
              : `${formatShortDate(d.date)}: ${formatMoney(d.revenue)} · ${d.orders} order(s)`;
            return (
              <div
                key={d.date}
                className={`admin-analytics-chart__col${isPeak ? ' admin-analytics-chart__col--peak' : ''}`}
                title={title}
              >
                <div className="admin-analytics-chart__bar-wrap">
                  <div
                    className={`admin-analytics-chart__bar${useOrders ? ' admin-analytics-chart__bar--orders' : ''}${
                      isPeak ? ' admin-analytics-chart__bar--peak' : ''
                    }`}
                    style={{ height: `${h}%` }}
                  />
                </div>
                <span className="admin-analytics-chart__label">{formatShortDate(d.date)}</span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="admin-analytics-chart__footnote small text-muted mb-0 mt-2">
        {useOrders ? (
          <>
            <strong>{summary.total}</strong> orders across <strong>{summary.activeDays}</strong> active day
            {summary.activeDays === 1 ? '' : 's'}
            {summary.peak ? (
              <>
                {' '}
                · Best day <strong>{formatShortDate(summary.peak.date)}</strong> ({summary.peak.orders} orders)
              </>
            ) : null}
          </>
        ) : (
          <>
            Avg <strong>{formatMoney(summary.avg)}</strong>/day on active days ·{' '}
            <strong>{summary.activeDays}</strong> day{summary.activeDays === 1 ? '' : 's'} with sales
            {summary.peak ? (
              <>
                {' '}
                · Best day <strong>{formatShortDate(summary.peak.date)}</strong> ({formatMoney(summary.peak.revenue)})
              </>
            ) : null}
          </>
        )}
      </p>
    </>
  );
}

export function AnalyticsRecentOrders({ orders, onGoOrders, formatMoneyFn = formatMoney }) {
  const recent = Array.isArray(orders) ? orders : [];
  return (
    <div id="analytics-recent" className="card border-0 shadow-sm h-100">
      <div className="card-header bg-transparent d-flex justify-content-between align-items-center border-0 pb-0">
        <div>
          <h3 className="h6 mb-0">Recent orders</h3>
          <p className="small text-muted mb-0">Latest activity across the shop</p>
        </div>
        {onGoOrders ? (
          <button type="button" className="btn btn-sm btn-outline-primary rounded-pill" onClick={onGoOrders}>
            All orders
          </button>
        ) : null}
      </div>
      <div className="card-body pt-2">
        {recent.length === 0 ? (
          <p className="small text-muted mb-0">No orders yet.</p>
        ) : (
          <ul className="list-unstyled mb-0 admin-analytics-recent">
            {recent.map((o) => (
              <li key={o.order_number}>
                <button type="button" className="admin-analytics-recent__row" onClick={onGoOrders}>
                  <span className="admin-analytics-recent__main">
                    <strong>#{o.order_number}</strong>
                    <span className="text-muted d-block">
                      {o.customer?.display_name || o.customer?.username || 'Customer'}
                      {o.shipping_city ? ` · ${o.shipping_city}` : ''}
                    </span>
                  </span>
                  <span className="admin-analytics-recent__meta text-end">
                    <span className={`admin-analytics-recent__status admin-analytics-recent__status--${String(o.status || '').toLowerCase()}`}>
                      {formatOrderStatusLabel(o.status)}
                    </span>
                    <span className="d-block fw-semibold">{formatMoneyFn(o.total_amount)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function AnalyticsSectionTitle({ children, hint }) {
  return (
    <div className="admin-analytics-section-title mb-3">
      <h2 className="admin-analytics-section-title__heading h6 mb-0">{children}</h2>
      {hint ? <p className="admin-analytics-section-title__hint small text-muted mb-0">{hint}</p> : null}
    </div>
  );
}

export function AnalyticsToolbar({
  rangePresets,
  presetActive,
  onPreset,
  customFrom,
  customTo,
  onCustomFrom,
  onCustomTo,
  customActive,
  onApplyCustom,
  loading,
  hasData,
  onRefresh,
  onExportCsv,
  onPrint,
}) {
  return (
    <div className="admin-analytics-toolbar">
      <div className="admin-analytics-toolbar__group">
        <span className="admin-analytics-toolbar__label">Show me</span>
        <div className="btn-group btn-group-sm" role="group" aria-label="Date range">
          {rangePresets.map((p) => (
            <button
              key={p.days}
              type="button"
              className={`btn btn-outline-secondary${presetActive(p.days) ? ' active' : ''}`}
              onClick={() => onPreset(p.days)}
              title={p.title}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <form className="admin-analytics-toolbar__custom" onSubmit={onApplyCustom}>
        <span className="admin-analytics-toolbar__label">Custom</span>
        <input
          id="analytics-from"
          type="date"
          className="form-control form-control-sm"
          value={customFrom}
          max={customTo}
          onChange={(e) => onCustomFrom(e.target.value)}
          aria-label="Start date"
        />
        <span className="admin-analytics-toolbar__sep" aria-hidden>
          to
        </span>
        <input
          id="analytics-to"
          type="date"
          className="form-control form-control-sm"
          value={customTo}
          min={customFrom}
          onChange={(e) => onCustomTo(e.target.value)}
          aria-label="End date"
        />
        <button type="submit" className={`btn btn-sm${customActive ? ' btn-primary' : ' btn-outline-secondary'}`}>
          Apply
        </button>
      </form>
      <div className="admin-analytics-toolbar__tools">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onRefresh} disabled={loading}>
          {loading ? 'Updating…' : 'Refresh'}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={onExportCsv}
          disabled={!hasData || loading}
        >
          Download CSV
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={onPrint}
          disabled={!hasData || loading}
          title="Print or save as PDF"
        >
          Print
        </button>
      </div>
    </div>
  );
}

export function AnalyticsAtAGlance({ data, rangeLabel, prevLabel, onGoOrders }) {
  if (!data?.period) return null;
  const period = data.period || {};
  const cmp = data.comparison || {};
  const revDelta = formatDelta(cmp.revenue_pct);
  const ordDelta = formatDelta(cmp.orders_pct);
  const pa = data.period_activity || {};

  const prevRev = cmp.previous?.revenue;
  const prevOrd = cmp.previous?.orders;

  return (
    <section
      id="analytics-glance"
      className="admin-analytics-glance card border-0 shadow-sm mb-3"
      aria-labelledby="analytics-glance-title"
    >
      <div className="card-body">
        <div className="admin-analytics-glance__head">
          <div>
            <h2 id="analytics-glance-title" className="admin-analytics-glance__title h5 mb-1">
              {rangeLabel}
            </h2>
            <p className="admin-analytics-glance__lead small text-muted mb-0">
              Paid sales in this window
              {prevLabel ? ` · compared with ${prevLabel}` : ''}
            </p>
          </div>
          {onGoOrders ? (
            <button type="button" className="btn btn-sm btn-outline-primary rounded-pill" onClick={onGoOrders}>
              Open orders
            </button>
          ) : null}
        </div>
        <div className="admin-analytics-glance__grid">
          <div className="admin-analytics-glance__metric admin-analytics-glance__metric--primary">
            <span className="admin-analytics-glance__metric-label">Revenue</span>
            <span className="admin-analytics-glance__metric-value">{formatMoney(period.revenue)}</span>
            {revDelta.tone !== 'muted' ? (
              <span className={`admin-analytics-delta admin-analytics-delta--${revDelta.tone}`}>{revDelta.text}</span>
            ) : null}
            {prevRev != null && prevRev > 0 ? (
              <span className="admin-analytics-glance__metric-sub small text-muted">
                Last period {formatMoney(prevRev)}
              </span>
            ) : null}
          </div>
          <div className="admin-analytics-glance__metric">
            <span className="admin-analytics-glance__metric-label">Paid orders</span>
            <span className="admin-analytics-glance__metric-value">{period.orders ?? 0}</span>
            {ordDelta.tone !== 'muted' ? (
              <span className={`admin-analytics-delta admin-analytics-delta--${ordDelta.tone}`}>{ordDelta.text}</span>
            ) : null}
            {prevOrd != null && prevOrd > 0 ? (
              <span className="admin-analytics-glance__metric-sub small text-muted">
                Last period {prevOrd}
              </span>
            ) : null}
          </div>
          <div className="admin-analytics-glance__metric">
            <span className="admin-analytics-glance__metric-label">Avg order value</span>
            <span className="admin-analytics-glance__metric-value">{formatMoney(period.aov)}</span>
          </div>
          <div className="admin-analytics-glance__metric">
            <span className="admin-analytics-glance__metric-label">Checkout success</span>
            <span className="admin-analytics-glance__metric-value">
              {pa.checkout_conversion_pct != null ? `${pa.checkout_conversion_pct}%` : '—'}
            </span>
            <span className="admin-analytics-glance__metric-sub small text-muted">
              {pa.orders_paid ?? 0} paid of {pa.orders_placed ?? 0} placed
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function AnalyticsLoadingState() {
  return (
    <div className="admin-analytics-loading mb-3" aria-busy="true" aria-label="Loading analytics">
      <div className="admin-analytics-loading__glance" />
      <div className="row g-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="col-sm-6 col-xl-3">
            <div className="admin-analytics-loading__stat" />
          </div>
        ))}
      </div>
      <div className="admin-analytics-loading__chart" />
      <p className="small text-muted text-center mb-0 mt-2">Pulling your shop numbers…</p>
    </div>
  );
}

export function AnalyticsGuide() {
  return (
    <details className="admin-analytics-guide card border-0 shadow-sm mb-3">
      <summary className="admin-analytics-guide__summary card-body py-3">
        <span className="admin-analytics-guide__summary-title">Tips — what these numbers mean</span>
        <span className="admin-analytics-guide__summary-hint small text-muted">Tap to expand</span>
      </summary>
      <div className="card-body pt-0 pb-3">
        <dl className="admin-analytics-guide__list small mb-0">
          <div>
            <dt>Revenue</dt>
            <dd>Money from orders that are paid, processing, shipped, or completed. Unpaid carts are not counted.</dd>
          </div>
          <div>
            <dt>Average order value</dt>
            <dd>How much each paid order is worth on average. Helpful for spotting upsell opportunities.</dd>
          </div>
          <div>
            <dt>Order pipeline</dt>
            <dd>A live picture of open orders — not limited to the dates you picked above.</dd>
          </div>
          <div>
            <dt>Partially shipped</dt>
            <dd>Some items went out; finish the rest from the Orders tab with tracking.</dd>
          </div>
          <div>
            <dt>Change % (green / red)</dt>
            <dd>Compares with the previous period of the same length right before your dates.</dd>
          </div>
        </dl>
      </div>
    </details>
  );
}

export function AnalyticsActionBoard({ actions, onGoOrders, onGoReviews }) {
  if (!actions?.length) {
    return (
      <div id="analytics-actions" className="card border-0 shadow-sm mb-3 admin-analytics-actions admin-analytics-actions--clear">
        <div className="card-body py-3 d-flex align-items-center gap-2">
          <span className="admin-analytics-actions__ok-icon" aria-hidden>
            ✓
          </span>
          <p className="small text-success mb-0 fw-semibold">You&apos;re all caught up — nothing urgent on the shop floor.</p>
        </div>
      </div>
    );
  }
  return (
    <div id="analytics-actions" className="card border-0 shadow-sm mb-3 admin-analytics-actions">
      <div className="card-body">
        <AnalyticsSectionTitle hint="Each row opens the place to fix it.">
          Do these next
        </AnalyticsSectionTitle>
        <ul className="list-unstyled mb-0 admin-analytics-actions__list">
          {actions.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                className={`admin-analytics-action admin-analytics-action--${a.severity}`}
                onClick={() => (a.id === 'reviews' ? onGoReviews() : onGoOrders())}
              >
                <span className="admin-analytics-action__count">{a.count}</span>
                <span className="admin-analytics-action__body">
                  <strong>{a.label}</strong>
                  <span className="d-block small">{a.detail}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function AnalyticsInsightsList({ data }) {
  const lines = buildAnalyticsInsights(data);
  if (!lines.length) {
    return (
      <div className="card border-0 shadow-sm mb-3 admin-analytics-insights-card admin-analytics-insights-card--empty">
        <div className="card-body py-3">
          <p className="small text-muted mb-0">
            Pick a date range above — we&apos;ll summarise sales, shipping, and payments in plain language here.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div id="analytics-insights" className="card border-0 shadow-sm mb-3 admin-analytics-insights-card">
      <div className="card-body">
        <AnalyticsSectionTitle hint="Auto-written summary — good for a quick morning check-in.">
          In plain English
        </AnalyticsSectionTitle>
        <ul className="admin-analytics-insights list-unstyled mb-0">
          {lines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function AnalyticsPipeline({ pipeline }) {
  const stages = pipeline || [];
  const max = Math.max(1, ...stages.map((s) => s.count || 0));
  if (!stages.length) {
    return <p className="small text-muted mb-0">No order data.</p>;
  }
  return (
    <div className="admin-analytics-pipeline" aria-label="Order pipeline">
      {stages.map((stage) => {
        const pct = Math.round(((stage.count || 0) / max) * 100);
        return (
          <div key={stage.key} className="admin-analytics-pipeline__stage" title={stage.hint}>
            <div className="admin-analytics-pipeline__head">
              <span className="admin-analytics-pipeline__label">{stage.label}</span>
              <strong className="admin-analytics-pipeline__count">{stage.count}</strong>
            </div>
            <div className="admin-analytics-pipeline__track">
              <div
                className={`admin-analytics-pipeline__fill admin-analytics-pipeline__fill--${stage.key}`}
                style={{ width: `${Math.max(stage.count > 0 ? 8 : 0, pct)}%` }}
              />
            </div>
            <span className="admin-analytics-pipeline__hint small text-muted">{stage.hint}</span>
          </div>
        );
      })}
    </div>
  );
}

export function AnalyticsShippingCard({ shipping, shipments, rangeLabel }) {
  const si = shipping || {};
  const tiles = [
    {
      label: 'Delivery charges',
      value: formatMoney(si.shipping_revenue),
      sub: si.shipping_pct_of_revenue > 0 ? `${si.shipping_pct_of_revenue}% of revenue` : rangeLabel,
    },
    {
      label: 'Free delivery orders',
      value: String(si.free_shipping_orders ?? 0),
      sub: rangeLabel,
    },
    {
      label: 'Paid → shipped (avg days)',
      value: si.avg_days_paid_to_ship != null ? String(si.avg_days_paid_to_ship) : '—',
      sub: 'For orders shipped in period',
    },
    {
      label: 'Still to pack/ship',
      value: String(si.fulfillment_backlog ?? 0),
      sub: 'Open fulfillment queue',
      warn: (si.fulfillment_backlog ?? 0) > 0,
    },
  ];
  if (shipments?.enabled) {
    tiles.push(
      {
        label: 'Parcels sent',
        value: String(shipments.parcels_in_period ?? 0),
        sub: rangeLabel,
      },
      {
        label: 'Units in parcels',
        value: String(shipments.units_shipped_in_period ?? 0),
        sub: 'Items dispatched',
      }
    );
  }
  return (
    <div className="admin-analytics-metric-tiles">
      {tiles.map((t) => (
        <div
          key={t.label}
          className={`admin-analytics-metric-tiles__item${t.warn ? ' admin-analytics-metric-tiles__item--warn' : ''}`}
        >
          <span className="admin-analytics-metric-tiles__label">{t.label}</span>
          <strong className="admin-analytics-metric-tiles__value">{t.value}</strong>
          {t.sub ? <span className="admin-analytics-metric-tiles__sub small text-muted">{t.sub}</span> : null}
        </div>
      ))}
    </div>
  );
}

export function AnalyticsPeriodSummary({ periodActivity, health }) {
  const pa = periodActivity || {};
  const h = health || {};
  const pills = [
    { label: 'Carts started', value: pa.orders_placed ?? h.placed ?? 0 },
    { label: 'Paid', value: pa.orders_paid ?? 0, accent: true },
    {
      label: 'Paid rate',
      value: pa.checkout_conversion_pct != null ? `${pa.checkout_conversion_pct}%` : '—',
    },
    {
      label: 'Cancelled',
      value: `${h.cancelled ?? 0}${h.cancellation_rate_pct != null ? ` (${h.cancellation_rate_pct}%)` : ''}`,
      warn: (h.cancellation_rate_pct ?? 0) > 5,
    },
  ];
  return (
    <div className="admin-analytics-period-pills" role="list">
      {pills.map((p) => (
        <div
          key={p.label}
          className={`admin-analytics-period-pill${p.accent ? ' admin-analytics-period-pill--accent' : ''}${
            p.warn ? ' admin-analytics-period-pill--warn' : ''
          }`}
          role="listitem"
        >
          <span className="admin-analytics-period-pill__label">{p.label}</span>
          <strong className="admin-analytics-period-pill__value">{p.value}</strong>
        </div>
      ))}
    </div>
  );
}

export { formatOrderStatusLabel, paymentMethodLabel };
